import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MapPinLine, Clock, Package, ShoppingBag, ForkKnife, Plus, Minus, Spinner,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RestaurantDetailPanel = ({
  open, onClose, exerciseId, venue, currencySymbol = 'Ÿ', onOrder,
  bookDisabledReason,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState({});
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!open || !exerciseId || !venue?.venue_id) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true);
    setCart({});
    (async () => {
      try {
        const d = await logisticsApi.foodVenueDetail(exerciseId, venue.venue_id);
        if (!cancelled) setDetail(d);
      } catch { if (!cancelled) setDetail(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, exerciseId, venue?.venue_id]);

  const updateQty = (itemId, delta) => {
    setCart((prev) => {
      const cur = prev[itemId] || 0;
      const next = Math.max(0, Math.min(50, cur + delta));
      if (next === 0) { const { [itemId]: _, ...rest } = prev; return rest; }
      return { ...prev, [itemId]: next };
    });
  };

  const cartItems = [];
  let cartTotal = 0;
  if (detail?.menu) {
    for (const cat of detail.menu) {
      for (const item of cat.items) {
        const qty = cart[item.item_id];
        if (qty > 0) {
          cartItems.push({ ...item, quantity: qty });
          cartTotal += item.price_minor * qty;
        }
      }
    }
  }

  const handleOrder = () => {
    if (cartItems.length === 0) return;
    onOrder?.(venue, cartItems);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[520px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-restaurant-detail"
            role="dialog" aria-label="Restaurant detail"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 h-14 shrink-0"
              style={{ borderBottom: '1px solid var(--ls-border)' }}
            >
              <div className="flex items-center gap-2">
                <ForkKnife size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Restaurant</span>
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2" aria-label="Close">
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Spinner size={24} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                </div>
              ) : detail ? (
                <>
                  {/* Gallery */}
                  {detail.images?.length > 0 && (
                    <div className="relative">
                      <div
                        className="w-full h-56 bg-cover bg-center"
                        style={{ backgroundImage: `url(${detail.images[activeImage]})` }}
                      />
                      {detail.images.length > 1 && (
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                          {detail.images.map((_, i) => (
                            <button
                              key={i} onClick={() => setActiveImage(i)}
                              className="w-2 h-2 rounded-full transition-all"
                              style={{
                                background: i === activeImage ? 'var(--ls-primary)' : 'rgba(255,255,255,0.5)',
                                transform: i === activeImage ? 'scale(1.3)' : 'scale(1)',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info */}
                  <div className="px-5 py-4">
                    <p className="ls-overline mb-1">{(detail.subcategory || '').replace(/_/g, ' ')}</p>
                    <h2 className="ls-heading text-xl font-bold">{detail.name}</h2>
                    {detail.address && (
                      <p className="text-xs flex items-center gap-1 mt-1" style={{ color: 'var(--ls-muted)' }}>
                        <MapPinLine size={12} weight="fill" /> {detail.address}
                      </p>
                    )}
                    {detail.description && (
                      <p className="text-sm mt-3" style={{ color: 'var(--ls-secondary)' }}>
                        {detail.description}
                      </p>
                    )}

                    {/* Operating info */}
                    {detail.info && (
                      <div className="flex flex-wrap gap-3 mt-3">
                        <span className="text-xs flex items-center gap-1 ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>
                          <Clock size={12} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                          {detail.info.opens} – {detail.info.closes}
                        </span>
                        {detail.info.takeaway_available && (
                          <span className="text-xs flex items-center gap-1 ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>
                            <ShoppingBag size={12} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                            Takeaway
                          </span>
                        )}
                        {detail.info.delivery_available && (
                          <span className="text-xs flex items-center gap-1 ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>
                            <Package size={12} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                            Delivery ~{detail.info.estimated_prep_minutes}min
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Menu */}
                  <div className="px-5 pb-5">
                    <h3 className="ls-heading text-sm font-bold mb-3" style={{ borderBottom: '1px solid var(--ls-border)', paddingBottom: 8 }}>
                      Menu
                    </h3>
                    {detail.menu?.map((cat) => (
                      <div key={cat.category} className="mb-4">
                        <p className="ls-overline mb-2">{cat.label}</p>
                        <div className="space-y-1">
                          {cat.items.map((item) => {
                            const qty = cart[item.item_id] || 0;
                            return (
                              <div
                                key={item.item_id}
                                className="flex items-center gap-2 py-2 px-3 rounded-lg transition-colors"
                                style={{
                                  background: qty > 0 ? 'var(--ls-elevated)' : 'transparent',
                                  border: `1px solid ${qty > 0 ? 'var(--ls-primary)' : 'var(--ls-border)'}`,
                                }}
                                data-testid={`ls-menu-item-${item.item_id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>
                                    {item.name}
                                  </p>
                                </div>
                                <span className="ls-mono text-sm font-semibold flex-shrink-0" style={{ color: 'var(--ls-primary)' }}>
                                  {currencySymbol}{fmt(item.price_minor)}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {qty > 0 && (
                                    <button
                                      className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                                      style={{ background: 'var(--ls-surface)', border: '1px solid var(--ls-border)' }}
                                      onClick={() => updateQty(item.item_id, -1)}
                                    >
                                      <Minus size={10} weight="bold" />
                                    </button>
                                  )}
                                  {qty > 0 && (
                                    <span className="ls-mono text-xs font-bold w-5 text-center">{qty}</span>
                                  )}
                                  <button
                                    className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                                    style={{
                                      background: qty > 0 ? 'var(--ls-primary)' : 'var(--ls-surface)',
                                      color: qty > 0 ? '#fff' : 'var(--ls-secondary)',
                                      border: `1px solid ${qty > 0 ? 'var(--ls-primary)' : 'var(--ls-border)'}`,
                                    }}
                                    onClick={() => updateQty(item.item_id, 1)}
                                    disabled={!!bookDisabledReason}
                                  >
                                    <Plus size={10} weight="bold" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-20">
                  <p className="text-sm" style={{ color: 'var(--ls-muted)' }}>Could not load details</p>
                </div>
              )}
            </div>

            {/* Footer — cart summary + order button */}
            {cartItems.length > 0 && (
              <div
                className="px-5 py-4 shrink-0"
                style={{ borderTop: '1px solid var(--ls-border)' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                    {cartItems.reduce((s, i) => s + i.quantity, 0)} items
                  </span>
                  <span className="ls-mono text-lg font-bold" style={{ color: 'var(--ls-primary)' }}>
                    {currencySymbol}{fmt(cartTotal)}
                  </span>
                </div>
                <button
                  className="ls-btn-primary w-full justify-center"
                  onClick={handleOrder}
                  disabled={!!bookDisabledReason}
                  data-testid="ls-restaurant-order-btn"
                >
                  <ForkKnife size={15} weight="fill" /> Order Now
                </button>
                {bookDisabledReason && (
                  <p className="text-[11px] text-center mt-1" style={{ color: 'var(--ls-danger)' }}>
                    {bookDisabledReason}
                  </p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default RestaurantDetailPanel;
